import { useMemo } from 'react'

import {
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  CubeSchemaTables,
} from 'cube/constants/QueryConstants'
import { ConversationsCreatedFromUrlBaseQuery } from 'cube/queries'

import {
  CubeQueryBuilder,
  resultSetAsTableData,
  useCubeQuery,
} from '@driftt/dds.reports'

import useTimePeriod from 'hooks/useTimePeriod'

import { ConversationStartedFromRow } from './ConversationStartedFromTable.types'

export type ConversationStartedFromTableRow = {
  conversationStartedFromUrl: string
  conversationCount: number
}

type ConversationStartedFromMetric = {
  [CubeSchemaTables.CONVERSATIONS]: ConversationStartedFromRow
}

export interface ConversationsStartedFromResult {
  tableData: ConversationStartedFromTableRow[]
  isLoadingTableData: boolean
  isErrorFetchingTableData: boolean
}

/***
 * Hook that uses cube to fetch conversations started from data.
 *
 * @returns {ConversationsStartedFromResult}
 */
export const useConversationsStartedFrom = () => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const { accessFilters } = useCubeReportAccessFiltersContext()

  const conversationsCreatedFromUrlCubeQuery = new CubeQueryBuilder({
    query: ConversationsCreatedFromUrlBaseQuery,
  })
    .withFilters([
      {
        member: CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_FROM_URL,
        operator: 'set',
      },
      ...pageFilters,
      ...accessFilters,
    ])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withLimit(10)
    .build()

  const {
    data,
    isLoading: isLoadingTableData,
    isError: isErrorFetchingTableData,
  } = useCubeQuery({
    query: conversationsCreatedFromUrlCubeQuery,
  })

  const tableData = useMemo(() => {
    const tableRows = resultSetAsTableData(
      data
    ) as ConversationStartedFromMetric[]
    return tableRows.map((row) => ({
      conversationStartedFromUrl:
        row[CubeSchemaTables.CONVERSATIONS].conversationCreatedFromUrl,
      conversationCount: row[CubeSchemaTables.CONVERSATIONS].count,
    }))
  }, [data])

  return useMemo(
    () => ({ tableData, isLoadingTableData, isErrorFetchingTableData }),
    [tableData, isLoadingTableData, isErrorFetchingTableData]
  )
}

export default useConversationsStartedFrom
